import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useClient } from '@hooks/useClient'
import { usePagination } from '@hooks/usePagination'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { SortByType } from '@components/Table/interface'
import { PendingListAdminResponse } from '@models/admin/PendingListAdminResponse'
import { getErrorMessage } from '@libs/utils'
import { PendingInvitationPageProps } from './interface'

export function withPendingInvitationPage(
  Component: React.FC<PendingInvitationPageProps>
) {
  function WithPendingInvitationPage() {
    const client = useClient()
    const { isAuthenticated } = useAuthentication()
    const [searchParams, setSearchParams] = useState({
      search: '',
      orderBy: 'DESC',
      orderKey: 'confirmationSentAt',
    })
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const { page, perpage, pageChange } = usePagination({})
    const confirmModal = useModal({ modal: 'confirm' })
    const alert = useAlert()
    const queryClient = useQueryClient()

    const {
      data: pendingList,
      refetch,
      isLoading,
    } = useQuery(
      ['pending-list-admin', searchParams, page, perpage],
      () =>
        client?.adminClient.getPendingListAdmin({
          ...searchParams,
          page,
          perpage,
        }),
      {
        enabled: isAuthenticated,
      }
    )

    const { data: roleOptions = [] } = useQuery(
      'roles',
      () => client?.adminClient.getRoles(),
      {
        enabled: isAuthenticated,
      }
    )

    function handleSearch(search: string) {
      setSearchParams(prev => ({ ...prev, search }))
    }

    function handleResetSearch() {
      setSearchParams({ search: '', orderBy: '', orderKey: '' })
    }

    const handleSort = useCallback(({ key, order }: SortByType) => {
      setSearchParams(prev => ({ ...prev, orderBy: order, orderKey: key }))
    }, [])

    function handleSelectRow(id: number) {
      setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]
      )
    }

    function handleSelectAll(checked: boolean) {
      setSelectedIds(checked ? pendingList?.data.map(({ id }) => id) ?? [] : [])
    }

    function handleDeleteUsers(ids: number | number[]) {
      confirmModal.show({
        content: (
          <div className='w-[400px] mb-[20px] '>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium '>
              ยืนยันการลบผู้ใช้หรือไม่?
            </p>
          </div>
        ),
        onConfirm: async () => {
          try {
            await client?.adminClient.removeAdmins(
              typeof ids === 'number' ? [ids] : ids
            )
            alert.success('ลบผู้ใช้สำเร็จ')
          } catch (error) {
            alert.error(getErrorMessage(error))
          } finally {
            confirmModal.hide()
            refetch()
          }
        },
        onClose: () => {
          confirmModal.hide()
        },
      })
    }

    async function handleChangeRole(id: number, rolesId: number) {
      try {
        const newData = await client?.adminClient.updateAdminRole(id, rolesId)
        if (newData) {
          queryClient.setQueryData<PendingListAdminResponse | undefined>(
            ['pending-list-admin', searchParams, page, perpage],
            (oldData?: PendingListAdminResponse) => {
              if (oldData) {
                return {
                  ...oldData,
                  data: oldData.data.map(item =>
                    item.id === id ? newData : item
                  ),
                }
              }
              return oldData
            }
          )
          alert.success('แก้ไข Role ผู้ใช้สำเร็จ')
        }
      } catch (error) {
        alert.error(getErrorMessage(error))
      }
    }

    async function resendInvitation(id: number) {
      try {
        await client?.adminClient.resendInvitation(id)
        alert.success('ส่งอีเมลใหม่สำเร็จ')
      } catch (error) {
        alert.error(getErrorMessage(error))
      }
    }

    const pageProps = {
      data: pendingList?.data || [],
      page,
      total: pendingList?.total || 0,
      perpage,
      selectedIds,
      roleOptions,
      pageChange,
      handleSearch,
      handleResetSearch,
      handleSelectRow,
      handleSelectAll,
      handleDeleteUsers,
      handleSort,
      handleChangeRole,
      resendInvitation,
      isLoading,
    }
    return <Component {...pageProps} />
  }

  return WithPendingInvitationPage
}
